// Your level over time: an ink line (your level after each day you practiced), each answer a gray dot, the target a
// dashed line labeled at the side. Drawn at its real pixel width, so text and dots never stretch. Point at a dot to see
// it; click or tap it to open that answer.
import { type MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { cx } from '@/lib/cx';
import { formatShortDate } from '@/lib/format';

/** One score at a time (ISO). `to` opens it. */
export type TrendPoint = { at: string; value: number; to?: string };

type TrendOptions = {
  width: number;
  /** Start and end of the time shown (ms); the end is now. */
  from: number;
  to: number;
  target?: number | undefined;
};

/** Round numbers around the values: multiples of 5, at least 20 points tall, within 0 to 100. */
export function trendRange(values: readonly number[]): { lo: number; hi: number } {
  let lo = Math.max(0, Math.floor((Math.min(...values) - 2) / 5) * 5);
  let hi = Math.min(100, Math.ceil((Math.max(...values) + 2) / 5) * 5);
  while (hi - lo < 20) {
    if (hi < 100) hi += 5;
    else lo -= 5;
  }
  return { lo, hi };
}

type XY = [number, number];
const f = (n: number) => n.toFixed(1);

/** A smooth line through the points that never overshoots them: each curve stays between its two ends. */
function smoothPath(points: readonly XY[]): string {
  const first = points[0];
  if (!first || points.length < 2) return '';
  let d = `M${f(first[0])} ${f(first[1])}`;
  points.forEach((p1, i) => {
    const p2 = points[i + 1];
    if (!p2) return;
    const p0 = points[i - 1] ?? p1;
    const p3 = points[i + 2] ?? p2;
    const lo = Math.min(p1[1], p2[1]);
    const hi = Math.max(p1[1], p2[1]);
    const clamp = (v: number) => Math.min(hi, Math.max(lo, v));
    d +=
      `C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(clamp(p1[1] + (p2[1] - p0[1]) / 6))} ` +
      `${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(clamp(p2[1] - (p3[1] - p1[1]) / 6))} ${f(p2[0])} ${f(p2[1])}`;
  });
  return d;
}

/** Pure geometry for the chart, so it can be tested without a browser. Wide (480px and up) labels the target and
 * your level in a column on the right; narrow puts the target's label above its line. */
export function trendGeometry(
  answers: readonly TrendPoint[],
  level: readonly TrendPoint[],
  { width, from, to, target }: TrendOptions,
) {
  const w = Math.max(260, Math.round(width));
  const wide = w >= 480;
  const h = wide ? 248 : 200;
  const padL = wide ? 36 : 28;
  const padR = wide ? 76 : 10;
  const padT = 14;
  const padB = 30;
  const right = w - padR;
  const base = h - padB;
  const { lo, hi } = trendRange([
    ...answers.map((a) => a.value),
    ...level.map((l) => l.value),
    ...(target === undefined ? [] : [target]),
  ]);
  const span = Math.max(1, to - from);
  const x = (at: string) => padL + Math.min(1, Math.max(0, (Date.parse(at) - from) / span)) * (right - padL);
  const y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (base - padT);
  const step = hi - lo > 60 ? 20 : 10;
  const grid: { value: number; y: number }[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) grid.push({ value: v, y: y(v) });
  const count = wide ? 5 : 3;
  const xTicks = Array.from({ length: count }, (_, i) => ({
    x: padL + (i / (count - 1)) * (right - padL),
    label: i === count - 1 ? 'Today' : formatShortDate(new Date(from + (span * i) / (count - 1))),
    anchor: (i === 0 ? 'start' : i === count - 1 ? 'end' : 'middle') as 'start' | 'middle' | 'end',
  }));
  const last = level.at(-1);
  const end = last ? { x: x(last.at), y: y(last.value), value: last.value } : null;
  const targetY = target === undefined ? null : y(target);
  // The level's label moves off the target's when the two would touch.
  const endLabelY =
    end && targetY !== null && Math.abs(end.y - targetY) < 16 ? targetY + (end.y >= targetY ? 16 : -16) : end?.y;
  return {
    w,
    h,
    wide,
    padL,
    padT,
    right,
    base,
    grid,
    xTicks,
    dots: answers.map((a) => ({ x: x(a.at), y: y(a.value), point: a })),
    line: smoothPath(level.map((p) => [x(p.at), y(p.value)])),
    end,
    endLabelY,
    targetY,
  };
}

type TrendChartProps = {
  /** Each answer, oldest first: a gray dot. */
  answers: readonly TrendPoint[];
  /** Your level, oldest first: the ink line. */
  level: readonly TrendPoint[];
  from: number;
  to: number;
  target?: number | undefined;
  /** What the chart shows, for screen readers. */
  label: string;
};

/** "Wed Oct 7" */
const dayOf = (at: string) =>
  `${new Date(at).toLocaleDateString('en-US', { weekday: 'short' })} ${formatShortDate(new Date(at))}`;

export function TrendChart({ answers, level, from, to, target, label }: TrendChartProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(320);
  const [hover, setHover] = useState<number | null>(null);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (answers.length === 0) return null;
  const g = trendGeometry(answers, level, { width, from, to, target });

  /** The answer nearest the pointer, within 28px. */
  const nearest = (e: MouseEvent<SVGSVGElement>): number | null => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - box.left;
    const py = e.clientY - box.top;
    let best: number | null = null;
    let bestDistance = 28;
    g.dots.forEach((d, i) => {
      const distance = Math.hypot(d.x - px, d.y - py);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  };
  const shown = hover === null ? undefined : g.dots[hover];
  const tipW = 96;
  const tipX = shown ? (shown.x + 12 + tipW < g.w ? shown.x + 12 : shown.x - 12 - tipW) : 0;
  const tipY = shown ? Math.min(g.base - 46, Math.max(g.padT, shown.y - 23)) : 0;

  return (
    <div className="trend-wrap" ref={wrap}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: a pointer shortcut; the same answers open from Recent answers and History. */}
      <svg
        className={cx('trend', shown?.point.to && 'pointing')}
        width={g.w}
        height={g.h}
        viewBox={`0 0 ${g.w} ${g.h}`}
        role="img"
        aria-label={label}
        onPointerMove={(e) => setHover(nearest(e))}
        onPointerLeave={() => setHover(null)}
        onClick={(e) => {
          const i = nearest(e);
          const open = i === null ? undefined : g.dots[i]?.point.to;
          if (open) void navigate(open);
        }}
      >
        {g.grid.map((t) => (
          <g key={t.value}>
            <line className="grid" x1={g.padL} x2={g.right} y1={t.y} y2={t.y} />
            <text x="0" y={t.y + 4}>
              {t.value}
            </text>
          </g>
        ))}
        <line className="base" x1={g.padL} x2={g.right} y1={g.base} y2={g.base} />
        {g.xTicks.map((t, i) => (
          <text
            key={t.x}
            className={cx(i === g.xTicks.length - 1 && 'strong')}
            x={t.x}
            y={g.h - 8}
            textAnchor={t.anchor}
          >
            {t.label}
          </text>
        ))}
        {g.targetY !== null && (
          <>
            <line className="goal" x1={g.padL} x2={g.right} y1={g.targetY} y2={g.targetY} />
            {g.wide ? (
              <text x={g.right + 10} y={g.targetY + 4}>
                Target {target}
              </text>
            ) : (
              <text x={g.padL + 4} y={g.targetY - 6}>
                Target {target}
              </text>
            )}
          </>
        )}
        {g.dots.map((d) => (
          <circle key={d.point.to ?? d.point.at} className="answer" cx={d.x} cy={d.y} r={2.5} />
        ))}
        {g.line && <path className="level" d={g.line} />}
        {g.end && (
          <>
            <circle className="end" cx={g.end.x} cy={g.end.y} r={5} />
            {g.wide && g.endLabelY !== undefined && (
              <text className="strong" x={g.right + 10} y={g.endLabelY + 4}>
                {g.end.value} <tspan className="soft">now</tspan>
              </text>
            )}
          </>
        )}
        {shown && (
          <g className="tip">
            <line className="hover-line" x1={shown.x} x2={shown.x} y1={g.padT} y2={g.base} />
            <circle className="end" cx={shown.x} cy={shown.y} r={5} />
            <rect x={tipX} y={tipY} width={tipW} height={46} rx={10} />
            <text x={tipX + 12} y={tipY + 19}>
              {dayOf(shown.point.at)}
            </text>
            <text className="strong" x={tipX + 12} y={tipY + 37}>
              {shown.point.value} <tspan className="soft">/100</tspan>
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
